import React from "react";
import {
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Table,
  Select,
  DatePicker,
  Space,
} from "antd";
import dayjs from "dayjs";
import formStyles from "@/components/DynamicForm/index.module.less";
import modalStyles from "../index.module.less";
import type { ItemType } from "@/components/DynamicForm";
import type { SupplierUserInfo, MaterialRecord } from "../types";

// ==================== 类型定义 ====================

/**
 * 申请人信息字段映射
 * 用于将 SupplierUserInfo 的字段映射到表单字段
 */
export type ApplicantFieldKey =
  | "applicantName"
  | "purchaseUserNo"
  | "createDate"
  | "deptName"
  | "postName"
  | "purchaseUserEmail";

// ==================== 常量配置 ====================

/**
 * 获取申请人信息字段配置
 * 定义了每个字段的标签和禁用状态
 */
const getApplicantFields = (
  t: any,
): Record<ApplicantFieldKey, { label: string; disabled: boolean }> => ({
  applicantName: { label: t("申请人"), disabled: true },
  purchaseUserNo: { label: t("工号"), disabled: true },
  createDate: { label: t("申请日期"), disabled: true },
  deptName: { label: t("部门"), disabled: true },
  postName: { label: t("岗位"), disabled: true },
  purchaseUserEmail: { label: t("邮箱"), disabled: false },
});

/**
 * 工厂选项配置
 * 格式: factoryName|factoryValue
 */
const getFactoryOptions = (t: any) => [
  { label: t("E1 Bright Future"), value: "1000" },
  { label: t("E2 BF Chinese Medicine"), value: "2000" },
  { label: t("E4 SumaUS"), value: "4000" },
  { label: t("E5 SumaHK"), value: "3000" },
];

/**
 * 申请类型选项配置
 * 格式: 申请类型名称|申请类型值
 */
const getApplicationTypeOptions = (t: any) => [
  { label: t("New Price 新价格"), value: "A" },
  { label: t("Price Decrease 降价"), value: "B" },
  { label: t("Price Increase 加价"), value: "C" },
];

/**
 * 采购类型选项配置
 * 按申请类型前缀分组: A_, B_, C_
 */
const getAllPurchaseTypeOptions = (t: any) => [
  // A 开头的采购类型
  { label: t("A materia ls A类物料"), value: "A_A" },
  {
    label: t(
      "B,C materials and new materials+AF OEM out products B,C类物料和新物料+AF委外采购产品",
    ),
    value: "A_BCAF",
  },
  {
    label: t("Lab study, scale up materials 研发中小试和中试物料"),
    value: "A_SLM",
  },
  { label: t("Type A,B Material A类物料/B类物料"), value: "A_AB" },
  { label: t("Type C,D Material C,D类物料"), value: "A_CD" },
  {
    label: t("RD scale up raw material(Equvalent to D material) 研发中试物料"),
    value: "A_ZHONG",
  },
  // B 开头的采购类型
  { label: t("All Material 所有物料"), value: "B_SUOYOU" },
  // C 开头的采购类型
  { label: t("Type A/B Material A类物料/B类物料"), value: "C_AB" },
  {
    label: t("Type C/D Material(inlcude scale up materials) C类物料/D类物料"),
    value: "C_CD",
  },
  { label: t("A materials A类物料"), value: "C_A" },
  {
    label: t(
      "B,C materials and new materials+AF OEM out products B,C类物料和新物料+AF委外采购产品",
    ),
    value: "C_BCAF",
  },
  {
    label: t("Lab study, scale up materials 研发中小试和中试物料"),
    value: "C_SLM",
  },
  { label: t("RD scale up raw material 研发中试物料"), value: "C_ZHONG" },
];

/**
 * 根据申请类型过滤采购类型选项
 * @param applicationType - 申请类型值 (A, B, C)
 * @param t - i18n 翻译函数
 * @returns 过滤后的采购类型选项
 */
export const getPurchaseTypeOptions = (applicationType?: string, t?: any) => {
  if (!applicationType) {
    return [];
  }
  const allOptions = t ? getAllPurchaseTypeOptions(t) : [];
  return allOptions.filter((option) =>
    option.value.startsWith(`${applicationType}_`),
  );
};

/**
 * 年度需求金额选项配置
 * 仅在申请类型为 "A"（新价格）时显示
 */
const getAnnualDemandAmountOptions = (t: any) => [
  { label: t("Smaller than 300K 小于30万"), value: "01" },
  { label: t("Equal or bigger than 300K 大于或等于30万"), value: "02" },
  { label: t("Smaller than 500K 小于50万"), value: "03" },
  { label: t("Equal or bigger than 500K 大于或等于50万"), value: "04" },
];

/**
 * 价格变动范围选项配置
 * 仅在申请类型为 "B"（降价）或 "C"（加价）时显示
 */
const getPriceChangeRangeOptions = (t: any) => [
  { label: t("All"), value: "01" },
  { label: t("Smaller than 小于10%"), value: "02" },
  { label: t("Equal or bigger than 10% 大于或等于10%"), value: "03" },
  { label: t("Smaller than 30% 小于30%"), value: "04" },
  { label: t("Equal or bigger than 30% 大于或等于30%"), value: "05" },
];

/**
 * 是否为首次采购选项配置
 * 值为 YES（是）或 NO（否）
 */
const getFirstPurchaseOptions = (t: any) => [
  { label: t("是"), value: "YES" },
  { label: t("否"), value: "NO" },
];

/**
 * 货币选项配置
 * 支持多种国际货币
 */
const getCurrencyOptions = () => [
  { label: "AUD", value: "AUD" },
  { label: "CAD", value: "CAD" },
  { label: "CNY", value: "CNY" },
  { label: "EUR", value: "EUR" },
  { label: "GBP", value: "GBP" },
  { label: "HKD", value: "HKD" },
  { label: "JPY", value: "JPY" },
  { label: "SEK", value: "SEK" },
  { label: "SGD", value: "SGD" },
  { label: "TWD", value: "TWD" },
  { label: "USD", value: "USD" },
];

/**
 * 判断是否应该显示年度需求金额字段
 * 仅在申请类型为 "A"（新价格）时显示
 * @param applicationType - 申请类型值
 * @returns 是否显示年度需求金额字段
 */
export const shouldShowAnnualDemandAmount = (applicationType?: string) => {
  return applicationType === "A";
};

/**
 * 判断是否应该显示价格变动范围字段
 * 仅在申请类型为 "B"（降价）或 "C"（加价）时显示
 * @param applicationType - 申请类型值
 * @returns 是否显示价格变动范围字段
 */
export const shouldShowPriceChangeRange = (applicationType?: string) => {
  return applicationType === "B" || applicationType === "C";
};

// ==================== 配置函数 ====================

/**
 * 获取物料表格列配置
 * @param onFieldChange - 字段变化回调函数
 * @param materialData - 最新的物料数据，用于在渲染时获取最新的record信息
 * @param onPriceUnitChange - 处理货币选择变化的回调函数
 * @param isView - 是否为查看模式，用于控制是否禁用表格编辑
 * @param onDeleteMaterial - 删除物料行的回调函数
 * @returns 物料表格列配置数组
 */
const getMaterialTableColumns = (
  t: any,
  onFieldChange?: (rowIndex: number, fieldName: string, value: any) => void,
  materialData?: MaterialRecord[],
  onPriceUnitChange?: (rowIndex: number, currency: string) => void,
  isView?: boolean,
  onDeleteMaterial?: (rowIndex: number) => void,
) => {
  const columns = [
    {
      title: t("物料编号"),
      dataIndex: "materialCode",
      key: "materialCode",
      width: 120,
      resizable: true,
    },
    {
      title: t("物料名称"),
      dataIndex: "materialName",
      key: "materialName",
      width: 150,
      resizable: true,
    },
    {
      title: t("项目"),
      dataIndex: "projectName",
      key: "projectName",
      width: 100,
      render: (text: any, record: MaterialRecord, index: number) => (
        <Input
          value={text || (isView ? "-" : "")}
          placeholder={t("请输入")}
          size="small"
          disabled={isView}
          onChange={(e) =>
            onFieldChange?.(index, "projectName", e.target.value)
          }
        />
      ),
    },
    {
      title: t("预计交货天数"),
      dataIndex: "estimatedDeliveryDays",
      key: "estimatedDeliveryDays",
      width: 200,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text || text === "";
        return (
          <div style={{ position: "relative" }}>
            <Input
              value={text || (isView && isEmpty ? "-" : text)}
              placeholder={t("请输入")}
              size="small"
              type="number"
              disabled={isView}
              status={isEmpty ? "error" : ""}
              onChange={(e) =>
                onFieldChange?.(index, "estimatedDeliveryDays", e.target.value)
              }
            />
            {isEmpty && !isView && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("标准数量（SAP包装单位）"),
      dataIndex: "standardQuantity",
      key: "standardQuantity",
      width: 150,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text || text === "";
        return (
          <div style={{ position: "relative" }}>
            <Input
              value={text}
              placeholder={t("请输入")}
              size="small"
              type="number"
              disabled={isView}
              status={isEmpty ? "error" : ""}
              onChange={(e) =>
                onFieldChange?.(index, "standardQuantity", e.target.value)
              }
            />
            {isEmpty && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("新价格"),
      dataIndex: "newPriceInput",
      key: "newPriceInput",
      width: 350,
      render: (text: any, record: MaterialRecord, index: number) => (
        <Space.Compact style={{ width: "100%" }}>
          <Input
            value={text}
            placeholder={t("请输入")}
            size="small"
            type="number"
            disabled={isView}
            style={{ flex: 1 }}
            onChange={(e) => {
              const newValue = e.target.value;
              onFieldChange?.(index, "newPriceInput", newValue);
            }}
          />
          <Select
            value={record.currency}
            placeholder={t("请选择")}
            size="small"
            disabled={isView}
            style={{ width: 80 }}
            onChange={(value) => {
              onFieldChange?.(index, "currency", value);
              onPriceUnitChange?.(index, value);
            }}
            options={getCurrencyOptions()}
          />
        </Space.Compact>
      ),
    },
    {
      title: t("新价格（HKD）"),
      dataIndex: "newPriceHkd",
      key: "newPriceHkd",
      width: 120,
      render: (text: any, record: MaterialRecord, index: number) => (
        <InputNumber
          value={text}
          placeholder={t("请输入")}
          size="small"
          disabled={isView}
          style={{ width: "100%" }}
          min={0}
          precision={2}
          onChange={(value) => onFieldChange?.(index, "newPriceHkd", value)}
        />
      ),
    },
    {
      title: t("价格单位（价格）"),
      dataIndex: "priceUnit",
      key: "priceUnit",
      width: 180,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text && text !== 0;
        return (
          <div style={{ position: "relative" }}>
            <InputNumber
              value={text}
              placeholder={t("请输入")}
              size="small"
              disabled={isView}
              style={{ width: "100%" }}
              min={0}
              precision={2}
              status={isEmpty ? "error" : ""}
              onChange={(value) => onFieldChange?.(index, "priceUnit", value)}
            />
            {isEmpty && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("由"),
      dataIndex: "conversionFrom",
      key: "conversionFrom",
      width: 160,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text && text !== 0;
        return (
          <div style={{ position: "relative" }}>
            <InputNumber
              value={text}
              placeholder={t("请输入")}
              size="small"
              disabled={isView}
              style={{ width: "100%" }}
              min={0}
              precision={0}
              status={isEmpty ? "error" : ""}
              onChange={(value) =>
                onFieldChange?.(index, "conversionFrom", value)
              }
            />
            {isEmpty && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("物料单位（订单）"),
      dataIndex: "materialUnitOrder",
      key: "materialUnitOrder",
      width: 150,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text;
        return (
          <div style={{ position: "relative" }}>
            <Select
              value={text}
              style={{ width: "100%" }}
              placeholder={t("请选择")}
              size="small"
              disabled={isView}
              status={isEmpty ? "error" : ""}
              onChange={(value) =>
                onFieldChange?.(index, "materialUnitOrder", value)
              }
              options={[
                { label: "BAG", value: "BAG" },
                { label: "BOT", value: "BOT" },
                { label: "BX", value: "BX" },
                { label: "CTN", value: "CTN" },
                { label: "EA", value: "EA" },
                { label: "G", value: "G" },
                { label: "KG", value: "KG" },
                { label: "L", value: "L" },
                { label: "LB", value: "LB" },
                { label: "M", value: "M" },
                { label: "PAK", value: "PAK" },
                { label: "PCS", value: "PCS" },
                { label: "RL", value: "RL" },
                { label: "SB", value: "SB" },
                { label: "ST", value: "ST" },
                { label: "TB", value: "TB" },
                { label: "TH", value: "TH" },
                { label: "TS", value: "TS" },
                { label: "TU", value: "TU" },
                { label: "PAC", value: "PAC" },
                { label: "BOU", value: "BOU" },
              ]}
            />
            {isEmpty && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("转换至"),
      dataIndex: "conversionTo",
      key: "conversionTo",
      width: 160,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text && text !== 0;
        return (
          <div style={{ position: "relative" }}>
            <InputNumber
              value={text || 1}
              style={{ width: "100%" }}
              placeholder={t("请输入")}
              size="small"
              disabled={isView}
              min={0}
              precision={0}
              status={isEmpty ? "error" : ""}
              onChange={(value) =>
                onFieldChange?.(index, "conversionTo", value)
              }
            />
            {isEmpty && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("基本单位"),
      dataIndex: "baseUnit",
      key: "baseUnit",
      width: 100,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text;
        return (
          <div style={{ position: "relative" }}>
            <span>{text}</span>
            {isEmpty && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("阶梯数量价格"),
      dataIndex: "stepPriceQuantity",
      key: "stepPriceQuantity",
      width: 160,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text && text !== 0;
        return (
          <div style={{ position: "relative" }}>
            <InputNumber
              value={text}
              style={{ width: "100%" }}
              placeholder={t("请输入")}
              size="small"
              disabled={isView}
              min={0}
              precision={3}
              status={isEmpty ? "error" : ""}
              onChange={(value) =>
                onFieldChange?.(index, "stepPriceQuantity", value)
              }
            />
            {isEmpty && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("新价格生效日期"),
      dataIndex: "newPriceEffectiveDate",
      key: "newPriceEffectiveDate",
      width: 200,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text;
        return (
          <div style={{ position: "relative" }}>
            <DatePicker
              value={text ? dayjs(text) : null}
              placeholder={t("请选择日期")}
              size="small"
              disabled={isView}
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              status={isEmpty ? "error" : ""}
              onChange={(date) =>
                onFieldChange?.(
                  index,
                  "newPriceEffectiveDate",
                  date ? date.format("YYYY-MM-DD") : "",
                )
              }
            />
            {isEmpty && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("价格有效期至"),
      dataIndex: "newPriceValidUntil",
      key: "newPriceValidUntil",
      width: 200,
      render: (text: any, record: MaterialRecord, index: number) => {
        const isEmpty = !text;
        return (
          <div style={{ position: "relative" }}>
            <DatePicker
              value={text ? dayjs(text) : dayjs("9999-12-31")}
              placeholder={t("请选择日期")}
              size="small"
              disabled={isView}
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              status={isEmpty ? "error" : ""}
              onChange={(date) =>
                onFieldChange?.(
                  index,
                  "newPriceValidUntil",
                  date ? date.format("YYYY-MM-DD") : "",
                )
              }
            />
            {isEmpty && (
              <span style={{ color: "#ff4d4f", marginLeft: "4px" }}>*</span>
            )}
          </div>
        );
      },
    },
    {
      title: t("物料等级"),
      dataIndex: "materialGrade",
      key: "materialGrade",
      width: 100,
    },
    {
      title: t("旧价格"),
      dataIndex: "oldPrice",
      key: "oldPrice",
      width: 180,
      render: (text: any, record: MaterialRecord) => (
        <Space.Compact style={{ width: "100%" }}>
          <Input
            value={text || "-"}
            size="small"
            disabled
            style={{ flex: 1 }}
          />
          <Input
            value={record.oldCurrency || "-"}
            size="small"
            disabled
            style={{ width: 80 }}
          />
        </Space.Compact>
      ),
    },
    {
      title: t("旧价格（HKD）"),
      dataIndex: "oldPriceHkd",
      key: "oldPriceHkd",
      width: 120,
      render: (text: any) => text || "-",
    },
    {
      title: t("价格变动比率%"),
      dataIndex: "priceChangeRatio",
      key: "priceChangeRatio",
      width: 120,
      render: (text: any, record: MaterialRecord) => {
        if (!record.newPriceHkd || !record.oldPriceHkd) {
          return "-";
        }
        const ratio = ((record.newPriceHkd / record.oldPriceHkd) * 100).toFixed(
          2,
        );
        return ratio;
      },
    },
    {
      title: t("回传状态"),
      dataIndex: "sapReturnStatus",
      key: "sapReturnStatus",
      width: 100,
      render: (text: any) => text || "-",
    },
    {
      title: t("操作"),
      key: "operation",
      width: 80,
      fixed: "right",
      render: (_: any, record: MaterialRecord, index: number) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            danger
            disabled={isView}
            onClick={() => onDeleteMaterial?.(index)}
          >
            {t("删除")}
          </Button>
        </Space>
      ),
    },
  ];

  return columns;
};

/**
 * 获取价格申请表单项配置
 * 返回完整的表单项配置数组，包含申请人信息和供应商信息
 *
 * 注意：表单项配置中定义的是 UI 结构和约束，
 * 实际的表单数据值由主组件 PriceApplicationModal 通过 formValue 填充
 *
 * @param supplierInfo - 供应商信息对象，用于检查是否有数据（可选，有或无都能生成表单）
 * @param onOpenSupplierModal - 打开供应商选择弹窗的回调函数
 * @param onOpenFactoryModal - 打开工厂选择弹窗的回调函数
 * @param applicationType - 当前申请类型，用于过滤采购类型选项
 * @param onOpenQuotationModal - 打开报价单选择弹窗的回调函数
 * @param onGetMaterial - 获取物料的回调函数
 * @param materialData - 物料数据数组，获取物料成功后返回
 * @param onMaterialFieldChange - 处理物料表格字段变化的回调函数
 * @param onPriceUnitChange - 处理货币选择变化的回调函数（触发汇率查询）
 * @param isView - 是否为查看模式，用于控制字段的禁用状态
 * @param onDeleteMaterial - 删除物料行的回调函数
 * @param isFromWorkbench - 是否从工作台进入，为true时显示审批意见和审批说明
 * @param onNavigateToPriceCompare - 导航到价格对比页面的回调函数，接收 recordId 参数
 * @param selectedQuotations - 选中的报价单数据数组
 * @param firstPurchaseConflictError - 是否存在首次采购冲突错误
 * @returns 表单项配置数组
 */
export const getPriceApplicationFormItems = (
  t: any,
  supplierInfo: SupplierUserInfo | null,
  onOpenSupplierModal?: () => void,
  onOpenFactoryModal?: () => void,
  applicationType?: string,
  onOpenQuotationModal?: () => void,
  onGetMaterial?: () => void,
  materialData?: MaterialRecord[],
  onMaterialFieldChange?: (
    rowIndex: number,
    fieldName: string,
    value: any,
  ) => void,
  onPriceUnitChange?: (rowIndex: number, currency: string) => void,
  isView?: boolean,
  onDeleteMaterial?: (rowIndex: number) => void,
  isFromWorkbench: boolean = false,
  onNavigateToPriceCompare?: (recordId: string | number) => void,
  selectedQuotations?: any[],
  firstPurchaseConflictError: boolean = false,
): ItemType[] => {
  // 即使 supplierInfo 为 null，也应该生成表单项
  // 表单项配置定义的是 UI 结构，实际数据由 formValue 填充
  const applicantFields = getApplicantFields(t);

  return [
    // ==================== 申请人信息 ====================
    {
      name: "applicantWrapperStart",
      col: { span: 24 },
      wrapperStart: { className: formStyles.wrapperPadded },
    },
    {
      label: applicantFields.applicantName.label,
      name: "applicantName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: applicantFields.applicantName.disabled,
      },
    },
    {
      label: applicantFields.purchaseUserNo.label,
      name: "purchaseUserNo",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: applicantFields.purchaseUserNo.disabled,
      },
    },
    {
      label: applicantFields.createDate.label,
      name: "createDate",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: applicantFields.createDate.disabled,
      },
    },
    {
      label: applicantFields.deptName.label,
      name: "deptName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: applicantFields.deptName.disabled,
      },
    },
    {
      label: applicantFields.postName.label,
      name: "postName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: applicantFields.postName.disabled,
      },
    },
    {
      label: applicantFields.purchaseUserEmail.label,
      name: "purchaseUserEmail",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input",
        disabled: applicantFields.purchaseUserEmail.disabled,
      },
    },
    {
      name: "applicantWrapperEnd",
      col: { span: 24 },
      wrapperEnd: true,
    },

    // ==================== 供应商信息 ====================
    {
      name: "supplierSection",
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionHeader}>
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>{t("申请信息")}</span>
          </div>
        ),
      },
    },
    {
      name: "supplierWrapperStart",
      col: { span: 24 },
      wrapperStart: { className: formStyles.wrapperLight },
    },
    {
      label: t("供应商编号"),
      name: "supplierCode",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      rules: [{ required: true, message: t("请输入供应商编号") }],
      widget: {
        widgetType: "Input",
        disabled: true,
      },
    },
    {
      label: t("供应商名称（中文）"),
      name: "supplierName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      rules: [{ required: true, message: t("请选择供应商名称（中文）") }],
      widget: {
        widgetType: "SupplierSelectInput",
        placeholder: t("请选择供应商"),
        onOpenModal: onOpenSupplierModal,
        disabled: isView,
      },
    },
    {
      label: t("供应商名称（英文）"),
      name: "supplierUsName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      rules: [{ required: true, message: t("请选择供应商名称（英文）") }],
      widget: {
        widgetType: "SupplierSelectInput",
        placeholder: t("请选择供应商"),
        onOpenModal: onOpenSupplierModal,
        disabled: isView,
      },
    },
    {
      label: t("工厂"),
      name: "factoryName",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      rules: [{ required: true, message: t("请选择工厂") }],
      widget: {
        widgetType: "Select",
        options: getFactoryOptions(t),
        placeholder: t("请选择工厂"),
        disabled: isView,
      },
    },
    {
      label: t("申请类型"),
      name: "applyTypeValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      rules: [{ required: true, message: t("请选择申请类型") }],
      widget: {
        widgetType: "Select",
        options: getApplicationTypeOptions(t),
        placeholder: t("请选择申请类型"),
        disabled: isView,
      },
    },
    {
      label: t("采购类型"),
      name: "purchaseTypeValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      rules: [{ required: true, message: t("请选择采购类型") }],
      widget: {
        widgetType: "Select",
        options: getPurchaseTypeOptions(applicationType, t),
        placeholder: t("请先选择申请类型"),
        readOnly: !applicationType,
        disabled: isView,
      },
    },
    {
      label: t("年度需求金额"),
      name: "annualDemandAmountValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      isShow: () => shouldShowAnnualDemandAmount(applicationType),
      rules: [
        {
          required: shouldShowAnnualDemandAmount(applicationType),
          message: t("请选择年度需求金额"),
        },
      ],
      widget: {
        widgetType: "Select",
        options: getAnnualDemandAmountOptions(t),
        placeholder: t("请选择年度需求金额"),
        disabled: isView,
      },
    },
    {
      label: t("价格变动范围"),
      name: "priceChangeRangeValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      isShow: () => shouldShowPriceChangeRange(applicationType),
      rules: [
        {
          required: shouldShowPriceChangeRange(applicationType),
          message: t("请选择价格变动范围"),
        },
      ],
      widget: {
        widgetType: "Select",
        options: getPriceChangeRangeOptions(t),
        placeholder: t("请选择价格变动范围"),
        disabled: isView,
      },
    },
    {
      label: t("是否为首次采购"),
      name: "isFirstPurchaseValue",
      col: { span: 8 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      className: "vertical-label-item",
      rules: [{ required: true, message: t("请选择是否为首次采购") }],
      widget: {
        widgetType: "Select",
        options: getFirstPurchaseOptions(t),
        placeholder: t("根据物料自动确定"),
        readOnly: !isView,
        disabled: isView,
      },
    },
    {
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionTitle}>{t("价格申请原因")}</div>
        ),
      },
    },
    {
      name: "priceApplyReason",
      col: { span: 24 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input.TextArea",
        rows: 3,
        disabled: isView,
        style: { resize: "none", marginTop: "8px" },
      },
    },
    {
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionTitle}>{t("询比议价过程")}</div>
        ),
      },
    },
    {
      col: { span: 24 },
      custom: {
        name: "quotationFile",
        render: (_: any, __: any, fieldValue: any) => {
          // 处理报价单文本点击 - 导航到价格对比页面
          const handleQuotationTextClick = () => {
            if (isView) {
              return;
            }
            // 如果有选中的报价单，使用第一个的 id 作为 recordId
            if (
              selectedQuotations &&
              selectedQuotations.length > 0 &&
              selectedQuotations[0].id
            ) {
              onNavigateToPriceCompare?.(selectedQuotations[0].id);
            }
          };

          return (
            <div className={modalStyles.negotiationContainer}>
              <Row gutter={0} className={modalStyles.negotiationRow}>
                <Col span={12} className={modalStyles.negotiationLeftCol}>
                  <Input.TextArea
                    name="inquiryProcess"
                    placeholder={t("请输入文本")}
                    rows={4}
                    disabled={isView}
                    className={modalStyles.negotiationTextarea}
                  />
                </Col>
                <Col span={12} className={modalStyles.negotiationRightCol}>
                  <div className={modalStyles.quotationContainer}>
                    <label className={modalStyles.quotationLabel}>
                      {t("报价单")}：
                    </label>
                    {fieldValue ? (
                      <span
                        className={modalStyles.quotationText}
                        onClick={handleQuotationTextClick}
                        style={!isView ? { cursor: "pointer" } : undefined}
                      >
                        {fieldValue}
                      </span>
                    ) : (
                      <Input
                        value={isView ? "-" : fieldValue}
                        placeholder={t("请选择报价单")}
                        className={modalStyles.quotationInput}
                        readOnly
                        disabled={isView}
                        onClick={!isView ? onOpenQuotationModal : undefined}
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          );
        },
      },
    },
    {
      col: { span: 24 },
      custom: {
        render: () => (
          <div className={formStyles.sectionTitle} style={{ marginTop: "8px" }}>
            {t("询比议价结果")}
          </div>
        ),
      },
    },
    {
      name: "inquiryResult",
      col: { span: 24 },
      labelCol: { span: 24 },
      wrapperCol: { span: 20 },
      widget: {
        widgetType: "Input.TextArea",
        rows: 3,
        disabled: isView,
        style: { resize: "none", marginTop: "8px" },
      },
    },
    {
      name: "priceDetails",
      col: { span: 24 },
      custom: {
        render: () => (
          <div
            className={formStyles.sectionHeader}
            style={{ marginTop: "8px" }}
          >
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>{t("价格明细")}</span>
          </div>
        ),
      },
    },
    {
      col: { span: 24 },
      isShow: () => !isView,
      custom: {
        render: () => (
          <div className={modalStyles.priceDetailSection}>
            <div className={modalStyles.priceDetailRow}>
              <div className={modalStyles.priceDetailInputCol}>
                <Input
                  placeholder={t("请输入物料编号")}
                  allowClear
                  disabled={isView}
                  className={modalStyles.priceDetailInput}
                  id="materialCodeInput"
                />
              </div>
              <div className={modalStyles.priceDetailButtonCol}>
                <Button
                  type="primary"
                  block
                  className={modalStyles.priceDetailButton}
                  onClick={onGetMaterial}
                >
                  {t("获取物料")}
                </Button>
              </div>
              {firstPurchaseConflictError && (
                <div className={modalStyles.priceDetailError}>
                  {t(
                    "获取失败，检测为首次采购，与列表物料冲突，所申请物料必须均为首次采购或非首次采购！",
                  )}
                </div>
              )}
            </div>
          </div>
        ),
      },
    },
    // 物料表格 - 始终渲染，如果没有数据则显示空表格
    {
      col: { span: 24 },
      custom: {
        render: () => (
          <div style={{ marginTop: "12px" }}>
            <Table
              columns={getMaterialTableColumns(
                t,
                onMaterialFieldChange,
                materialData,
                onPriceUnitChange,
                isView,
                onDeleteMaterial,
              )}
              dataSource={
                materialData && materialData.length > 0
                  ? materialData.map((item, index) => ({
                      ...item,
                      key: index,
                    }))
                  : []
              }
              pagination={false}
              size="small"
              scroll={{ x: 1500 }}
              bordered
            />
          </div>
        ),
      },
    },
    ...(isFromWorkbench
      ? [
          {
            name: "approvalSection",
            col: { span: 24 },
            custom: {
              render: () => (
                <div className={formStyles.subSectionTitle}>
                  {t("审批信息")}
                </div>
              ),
            },
          },
          {
            label: t("审批意见"),
            name: "reviewOpinion",
            col: { span: 24 },
            labelCol: { span: 3 },
            wrapperCol: { span: 20 },
            className: "inlineLabel",
            rules: [{ required: true, message: t("请选择审批意见") }],
            widget: {
              widgetType: "Radio.Group",
              options: [
                { label: t("同意"), value: "agree" },
                { label: t("驳回"), value: "reject" },
              ],
              disabled: false,
            },
          },
          {
            label: t("审批说明"),
            name: "reviewComments",
            col: { span: 24 },
            labelCol: { span: 3 },
            wrapperCol: { span: 22 },
            className: "inlineLabel",
            widget: {
              widgetType: "Input.TextArea",
              placeholder: t("请输入"),
              rows: 4,
              style: { resize: "none" },
              disabled: false,
            },
          },
        ]
      : []),
    {
      name: "supplierWrapperEnd",
      col: { span: 24 },
      wrapperEnd: true,
    },
  ];
};
